import { ReactNode, createElement } from 'react';
import type { ComponentType } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  MenuItem,
  Select,
  Toolbar,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import {
  AddCircle as AddCircleIcon,
  Event as EventIcon,
  Home as HomeIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { signOut } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { useTenant } from '../providers/TenantProvider';

interface MemberPortalLayoutProps {
  children: ReactNode;
}

interface MemberNavItem {
  label: string;
  path: string;
  Icon: ComponentType<SvgIconProps>;
}

const memberNavItems: MemberNavItem[] = [
  { label: 'Home', path: '/member', Icon: HomeIcon },
  { label: 'Book', path: '/member/book', Icon: AddCircleIcon },
  { label: 'Appointments', path: '/member/appointments', Icon: EventIcon },
];

function renderIcon(Icon: ComponentType<SvgIconProps>, props?: SvgIconProps) {
  return createElement(Icon, props);
}

export function MemberPortalLayout({ children }: MemberPortalLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, tenantId, tenants, switchTenant } = useTenant();
  const activeTenant = tenants.find((tenant) => tenant.tenantId === tenantId) || null;
  const memberTenants = tenants.filter(
    (tenant) => tenant.roles.includes('member') && !!tenant.membership.memberId
  );
  const portalTenant = memberTenants.find((tenant) => tenant.tenantId === tenantId) || activeTenant;
  const logoUrl = portalTenant?.branding?.logoUrl || '/logo.png';

  const isActive = (path: string) => {
    if (path === '/member') {
      return location.pathname === '/member';
    }
    return location.pathname.startsWith(path);
  };

  const handleTenantChange = (event: SelectChangeEvent) => {
    switchTenant(event.target.value);
    navigate('/member');
  };

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'white' }}
      >
        <Toolbar
          sx={{
            gap: 2,
            flexWrap: 'wrap',
            minHeight: { xs: 72, sm: 80 },
            py: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              minWidth: { xs: '100%', sm: 'auto' },
              flexGrow: { xs: 1, md: 0 },
            }}
          >
            <Box
              component="img"
              src={logoUrl}
              alt={portalTenant?.name || 'Fitstac'}
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                objectFit: 'contain',
                bgcolor: 'grey.100',
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {portalTenant?.name || 'Member Portal'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.email}
              </Typography>
            </Box>
          </Box>

          <Box
            component="nav"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexGrow: 1,
              overflowX: 'auto',
              pb: { xs: 0.5, sm: 0 },
            }}
          >
            {memberNavItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Button
                  key={item.path}
                  variant={active ? 'contained' : 'text'}
                  color={active ? 'primary' : 'inherit'}
                  startIcon={renderIcon(item.Icon)}
                  onClick={() => navigate(item.path)}
                  sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 600 }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { md: 'auto' } }}>
            {memberTenants.length > 1 && tenantId ? (
              <Select
                size="small"
                value={tenantId}
                onChange={handleTenantChange}
                sx={{ minWidth: 180 }}
              >
                {memberTenants.map((tenant) => (
                  <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                sx={{ display: { xs: 'none', sm: 'block' }, maxWidth: 180 }}
                noWrap
              >
                {portalTenant?.name || 'Member Portal'}
              </Typography>
            )}
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main' }}>
              {user?.email?.charAt(0).toUpperCase()}
            </Avatar>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={handleSignOut}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Sign Out
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth={false} disableGutters>
        {children}
      </Container>
    </Box>
  );
}

export default MemberPortalLayout;
